import { inject, Injectable, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { AuthService } from "@core/services/auth.service";
import { LoggerService } from "@core/services/logger.service";
import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { LoginRoutesConstants } from "@login/routes/login.routes.constants";
import { AuthProvider } from "@shared/models/types.model";

@Injectable({
  providedIn: "root"
})
export class AuthHandlerService {
  readonly isLoading = signal(false);
  readonly currentProvider = signal<AuthProvider | null>(null);
  readonly organizationId = signal<string | null>(null);

  private readonly _authService = inject(AuthService);
  private readonly _logger = inject(LoggerService);
  private readonly _router = inject(Router);

  constructor() {
    const route = inject(ActivatedRoute);

    // Check for query parameters
    route.queryParams.subscribe((params) => {
      if (params["organizationId"]) {
        this.organizationId.set(params["organizationId"]);
      }
    });
  }

  authenticateWithProvider(provider: AuthProvider): void {
    this.isLoading.set(true);
    this.currentProvider.set(provider);
    this._authService.loginWithProvider(provider, this.organizationId()).subscribe({
      next: (authUrl: string) => {
        if (authUrl) {
          // login via provider
          window.location.href = authUrl;
        } else {
          // local login
          this._router.navigate([DocumentationRoutesConstants.main()]).then(() => {
            this.isLoading.set(false);
          });
        }
      },
      error: (error: Error) => {
        this.isLoading.set(false);
        this.currentProvider.set(null);
        this._logger.error("Authentication error:", error);
      }
    });
  }

  authenticateWithCredentials(username: string, password: string): void {
    this.isLoading.set(true);
    this._authService.loginWithCredentials(username, password).subscribe({
      next: () => {
        this._router.navigate([DocumentationRoutesConstants.main()]).then(() => {
          this.isLoading.set(false);
        });
      },
      error: (error: Error) => {
        this.isLoading.set(false);
        this._logger.error("Login error:", error);
      }
    });
  }

  navigateToSignup(): void {
    const queryParams: { organizationId?: string | null } = {};
    if (this.organizationId()) {
      queryParams.organizationId = this.organizationId();
    }

    this._router.navigate([LoginRoutesConstants.signup()], {
      queryParams
    });
  }

  navigateToLogin(): void {
    const queryParams: { organizationId?: string | null } = {};
    if (this.organizationId()) {
      queryParams.organizationId = this.organizationId();
    }

    this._router.navigate([LoginRoutesConstants.login()], {
      queryParams
    });
  }

  getQueryParams(): Record<string, string | null> {
    const queryParams: Record<string, string | null> = {};
    if (this.organizationId()) {
      queryParams.organizationId = this.organizationId();
    }
    return queryParams;
  }
}
